import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';

import { getJson, useMeta } from '@/lib/api';
import { dayHeading, fmtTime } from '@/lib/format';
import type { FeedItem, FeedPage } from '@/lib/types';

import { ErrorCard, PillTag } from './ui';

function tile(item: FeedItem): { bg: string; fg: string; glyph: string } {
  const failed = item.status === 'error' || item.status === 'abandoned';
  if (item.type === 'ask')
    return failed
      ? { bg: 'var(--bad-bg)', fg: 'var(--bad)', glyph: '●' }
      : { bg: 'var(--good-bg)', fg: 'var(--good)', glyph: '●' };
  if (failed) return { bg: 'var(--bad-bg)', fg: 'var(--bad)', glyph: '▲' };
  if (item.type === 'run') return { bg: 'var(--brand-soft)', fg: 'var(--brand)', glyph: '✉' };
  if (item.type === 'system_event') return { bg: 'var(--sunk)', fg: 'var(--ink-3)', glyph: '↻' };
  return { bg: 'var(--good-bg)', fg: 'var(--good)', glyph: '◆' };
}

export function Timeline({ params }: { params: Record<string, string> }) {
  const meta = useMeta();
  const feed = useInfiniteQuery({
    queryKey: ['/feed', 'timeline', params],
    queryFn: ({ pageParam }) => getJson<FeedPage>('/feed', { ...params, cursor: pageParam, limit: 30 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  if (feed.error) return <ErrorCard error={feed.error} />;
  if (!feed.data) return <div className="skel" style={{ height: 160 }} />;
  const items = feed.data.pages.flatMap((p) => p.items);
  if (items.length === 0) return <div className="muted">Nothing in this range.</div>;
  const now = meta.data?.now ?? new Date().toISOString();
  let lastHeading = '';
  return (
    <>
      {items.map((item) => {
        const heading = dayHeading(item.at, now);
        const separator = heading !== lastHeading;
        lastHeading = heading;
        const t = tile(item);
        return (
          <Fragment key={item.key}>
            {separator && <div className="wk">{heading}</div>}
            <div className="tl">
              <span className="tm">{fmtTime(item.at)}</span>
              <span className="ic" style={{ background: t.bg, color: t.fg }}>
                {t.glyph}
              </span>
              <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {item.type === 'ask' ? (
                  item.text ? (
                    `“${item.text}”`
                  ) : (
                    <span className="faint">(text cleared after 90 days)</span>
                  )
                ) : (
                  item.text
                )}
                {item.actor && <span className="faint"> · {item.actor}</span>}
                {item.pills.length > 0 && (
                  <span style={{ display: 'inline-flex', gap: 4, marginLeft: 6, flexWrap: 'wrap' }}>
                    {item.pills.map((p) => (
                      <PillTag key={p.label} pill={p} />
                    ))}
                  </span>
                )}
              </div>
              {item.type === 'ask' ? (
                <Link className="link" to={`/feed?sel=ask::${item.id}&customers=false`}>
                  Replay
                </Link>
              ) : (
                <span />
              )}
            </div>
          </Fragment>
        );
      })}
      {feed.hasNextPage && (
        <button
          type="button"
          className="link"
          style={{ background: 'none', border: 0, padding: '8px 0' }}
          disabled={feed.isFetchingNextPage}
          onClick={() => feed.fetchNextPage()}
        >
          Load earlier
        </button>
      )}
    </>
  );
}
